import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { agGridThemes } from '../../../@shared/ag-grid-themes';
import { deriveDynamicColumnDefs } from '../../../@shared/ag-grid-columns';
import { GridCopyMenuComponent } from '../../../@shared/grid-copy-menu/grid-copy-menu.component';
import { CopySourceButtonComponent } from '../../../@shared/copy-source-button/copy-source-button.component';
import {
  IndexConstituentsService,
  IndexConstituentRow,
  IndexKey,
  IndexSummary,
} from '../../../@services/index-constituents.service';

// Index Constituents pane — read-only viewer over the
// LSEG:INDEX_CONSTITUENTS:<KEY> Redis keys. Segmented picker across the
// supported indexes:
//   - SPX/DJI/NDX/IXIC — populated by lseg-service pump_index_constituents
//     (mirrors symbol-list:<name> SET from f2-admin-service SymbolListsService)
//   - QQQ/IWV/IWM — populated by pump_etf_constituents from Massive."ETF
//     Constituents" collection (IT-F2-39, lseg-service commit 3b5b605)
// Columns derived from row shape (currently just TickerSymbol; future keys
// might carry more).
@Component({
  standalone: true,
  selector: 'app-index-constituents',
  imports: [AgGridAngular, CommonModule, GridCopyMenuComponent, CopySourceButtonComponent],
  templateUrl: './index-constituents.component.html',
  host: { class: 'block h-full' },
})
export class IndexConstituentsComponent implements OnInit {
  readonly indexes: { key: IndexKey; label: string }[] = [
    { key: 'SPX', label: 'S&P 500' },
    { key: 'DJI', label: 'Dow 30' },
    { key: 'NDX', label: 'Nasdaq 100' },
    { key: 'IXIC', label: 'Nasdaq Composite' },
    { key: 'QQQ', label: 'Invesco QQQ' },
    { key: 'IWV', label: 'Russell 3000' },
    { key: 'IWM', label: 'Russell 2000' },
  ];

  index: IndexKey = 'SPX';
  rows: IndexConstituentRow[] = [];
  columnDefs: any[] = [];
  summaries: IndexSummary[] = [];
  loading = false;
  loadedAt: string | null = null;

  theme = agGridThemes.dark;
  grid: any;

  constructor(private svc: IndexConstituentsService) {
    this.init_grid();
  }

  async ngOnInit() {
    // Kick off summary + data load in parallel — the summary powers
    // the picker's per-index count labels; the row load populates
    // the grid.
    void this.loadSummaries();
    await this.reload();
  }

  async loadSummaries() {
    this.summaries = await this.svc.listAll();
  }

  countFor(idx: IndexKey): number {
    const s = this.summaries.find(x => x.index === idx);
    return s?.count ?? 0;
  }

  async setIndex(idx: IndexKey) {
    if (idx === this.index) return;
    this.index = idx;
    await this.reload();
  }

  async reload() {
    this.loading = true;
    try {
      this.rows = await this.svc.getOne(this.index);
      // deriveDynamicColumnDefs pins _id/ticker/symbol by default; adds a
       // fieldOverride here so TickerSymbol also lands pinned left + gets
      // the teal color the other panels use for their primary ticker.
      this.columnDefs = deriveDynamicColumnDefs(this.rows, {
        fieldOverride: (field, coldef) => {
          if (field === 'TickerSymbol') {
            return {
              ...coldef,
              headerName: 'Ticker',
              pinned: 'left',
              minWidth: 120,
              maxWidth: 160,
              cellClass: 'font-mono',
              cellStyle: { 'text-align': 'left', color: '#5eead4' },
            };
          }
          return coldef;
        },
      });
      this.loadedAt = new Date().toISOString();
      // Refresh the summary too so the picker labels update after the
      // reload (e.g. if lseg-service re-pumped between clicks).
      void this.loadSummaries();
    } finally {
      this.loading = false;
    }
  }

  init_grid() {
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'multiRow',
          checkboxes: true,
          headerCheckbox: true,
          enableClickSelection: false,
        },
        defaultColDef: {
          floatingFilter: true,
          filter: true,
          sortable: true,
          resizable: true,
          cellStyle: { 'text-align': 'left' },
        },
        autoSizeStrategy: {
          type: 'fitCellContents',
          skipHeader: false,
        },
        onGridReady: (e: any) => { this.grid.api = e.api; },
        onFirstDataRendered: (e: any) => e.api.autoSizeAllColumns(false),
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
          { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
        ],
      },
    };
  }
}
